import React, {useState, useEffect, useCallback} from 'react';
import {Field, Form} from 'react-final-form';
import t from 'babel-plugin-yate/macro';
import {useDispatch} from 'react-redux';
import {
  getTransactionValidateEmail,
  getTransactionEmailCode,
  postTransactionEmailCode,
} from '../store/transaction';
import {
  composeFormValidators,
  isRequired,
  isAlphaNumeric,
} from '../lib/validations';
import {formatWhiteSpaceFormField} from '../lib/misc';
import {addModal} from '../store/app';

import ValidationError from '../components/Form/ValidationError';
import {Input} from '../components/Form/Input';
import Button from '../components/Button';
import Icon from '../components/Icons';
import Modal from '../components/Modal';

let timerId;

function TransactionEmail(props) {
  const {transaction, handlerSetIsEmailValidated, disableValidationBtn} = props;
  const dispatch = useDispatch();
  const [disableGenerateBtn, setDisableGenerateBtn] = useState(false);
  const [isWaitingResponse, setIsWaitingResponse] = useState(false);
  const [codeValidationFailed, setCodeValidationFailed] = useState(null);
  const [codeGenerationFailed, setCodeGenerationFailed] = useState(null);
  const [validatedEmail, setValidatedEmail] = useState(undefined);
  const [checkedEmail, setCheckedEmail] = useState(undefined);
  const [isModalOpen, setModalOpen] = useState(false);
  const disableAll = transaction.status !== 'new';
  const isValidationDisabled = disableAll || disableValidationBtn;

  const updateValidatedEmail = useCallback(() => {
    setValidatedEmail(transaction.email);
    setModalOpen(false);
    handlerSetIsEmailValidated(true);
  }, [handlerSetIsEmailValidated, transaction.email]);

  const handleValidateEmail = useCallback(async () => {
    const res = await dispatch(getTransactionValidateEmail(transaction.uuid));

    if (res.error) {
      dispatch(addModal('transaction_email_validation'));
      setModalOpen(true);

      if (checkedEmail !== transaction.email) {
        setCodeGenerationFailed(null);
        setDisableGenerateBtn(false);
      }
      return;
    }

    updateValidatedEmail();
  }, [
    dispatch,
    transaction.uuid,
    updateValidatedEmail,
    transaction.email,
    checkedEmail,
  ]);

  const handleGenerateCode = useCallback(async () => {
    setDisableGenerateBtn(true);
    setCodeGenerationFailed(null);
    setCheckedEmail(transaction.email);

    setIsWaitingResponse(true);
    const res = await dispatch(getTransactionEmailCode(transaction.uuid));
    setIsWaitingResponse(false);

    if (res.error) {
      setCodeGenerationFailed(res.errorMessage);
    }
  }, [dispatch, transaction.uuid, transaction.email]);

  const handleValidateCode = useCallback(
    async ({code}) => {
      setCodeValidationFailed(null);
      setIsWaitingResponse(true);
      const res = await dispatch(
        postTransactionEmailCode(transaction.uuid, {code}),
      );
      setIsWaitingResponse(false);

      if (res.error) {
        setCodeValidationFailed(res.errorMessage);
        return;
      }

      updateValidatedEmail();
    },
    [dispatch, transaction.uuid, updateValidatedEmail],
  );

  useEffect(() => {
    if (disableGenerateBtn === false) {
      return;
    }

    clearTimeout(timerId);
    timerId = setTimeout(() => {
      setDisableGenerateBtn(false);
      setCodeGenerationFailed(null);
      setCheckedEmail(undefined);
    }, 120 * 1000);

    return () => clearTimeout(timerId);
  }, [disableGenerateBtn]);

  return (
    <div className="flex flex-wrap relative">
      <div className="flex w-full">
        <div className="flex-grow">
          <Field
            name="email"
            label={t`Email address`}
            component={Input}
            disabled={true}
            initialValue={transaction?.email}
          />
        </div>

        {!isValidationDisabled && (
          <div className="ml-2 mt-5">
            {validatedEmail === transaction?.email && (
              <div className="flex items-center">
                <Icon name="Checked" width={30} height={40} fill="fill-green" />
                <div className="text-grey-darker-1 pl-2 text-12">{t`Validated`}</div>
              </div>
            )}

            {validatedEmail !== transaction?.email && (
              <Button
                title={t`Validate`}
                icon={true}
                iconSrc={<Icon name="FileboardChecklist" />}
                customProps={{
                  type: 'button',
                  disabled: isValidationDisabled,
                }}
                iconPosition="left"
                onClick={handleValidateEmail}
                color="white"
                border={true}
              />
            )}
          </div>
        )}
      </div>

      {/* Field bellow is used to warn the user
      if the email was not validated, when submitting the big form */}
      <Field name="email_validation_warn">
        {({input, meta}) => (
          <>
            <input className="w-0 h-0 absolute invisible" {...input} />

            {meta.error && meta.touched && (
              <div className="w-full">
                <ValidationError value={meta.error} />
              </div>
            )}
          </>
        )}
      </Field>

      {isModalOpen && (
        <Modal
          id="transaction_email_validation"
          header={' '}
          footer={null}
          width="md:w-2/3 lg:w-2/5"
          reqCloseCallback={() => {
            setModalOpen(false);
          }}>
          <Form
            keepDirtyOnReinitialize={true}
            onSubmit={handleValidateCode}
            render={({values, handleSubmit}) => (
              <form onSubmit={handleSubmit} className="w-full p-4">
                <div className="md:flex w-full">
                  <div className="mb-4 md:w-1/2">
                    <Field
                      name="email"
                      label={t`Email address`}
                      component={Input}
                      disabled={true}
                      initialValue={transaction?.email}
                    />

                    {codeGenerationFailed && (
                      <ValidationError value={codeGenerationFailed} />
                    )}

                    <div
                      className="pt-2"
                      title={t`Puteti trimite un nou cod peste 2 min`}>
                      <Button
                        title={t`Genereaza cod`}
                        customProps={{
                          type: 'button',
                          disabled:
                            isWaitingResponse ||
                            disableGenerateBtn ||
                            disableAll,
                        }}
                        onClick={handleGenerateCode}
                        color="blue"
                        border={false}
                      />
                    </div>
                  </div>

                  <div className="md:pl-4 md:w-1/2">
                    <Field
                      autoComplete="one-time-code"
                      label={t`Introduceti codul primit prin Email`}
                      name="code"
                      component={Input}
                      format={formatWhiteSpaceFormField}
                      validate={composeFormValidators(
                        isRequired(),
                        isAlphaNumeric(),
                      )}
                    />

                    {codeValidationFailed && (
                      <ValidationError value={codeValidationFailed} />
                    )}

                    <div className="pt-2">
                      <Button
                        title={t`Validate`}
                        customProps={{
                          type: 'button',
                          disabled: !values.code || disableAll,
                        }}
                        onClick={() => handleValidateCode(values)}
                        color="green"
                        border={false}
                      />
                    </div>
                  </div>
                </div>
              </form>
            )}
          />
        </Modal>
      )}
    </div>
  );
}

export default TransactionEmail;
